import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  RefreshCw, Search, ArrowUpDown, Download, Plus, Trash2, Check, Undo2, Filter,
  Pencil, CircleSlash, Copy, ClipboardPaste, Scissors,
  Columns3, ArrowLeftRight,
  EyeOff, PinIcon, ArrowUp, ArrowDown, Maximize2, Lock,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { ClientSideRowModelModule, ModuleRegistry } from 'ag-grid-community';
import type {
  ColDef, CellStyle, CellClassParams, GridReadyEvent, GridApi, SortChangedEvent,
  CellClickedEvent, CellDoubleClickedEvent, CellContextMenuEvent, DragStoppedEvent,
} from 'ag-grid-community';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { useToastStore } from '@/stores/toast-store';
import { GRID_THEMES } from '@/config/themes';
import { ExportModal } from '@/components/modals/ExportModal';
import { ReviewChangesModal } from '@/components/modals/ReviewChangesModal';
import { JsonViewerModal } from '@/components/shared/JsonViewerModal';
import { JsonChipRenderer } from './JsonChipRenderer';
import { ContextMenu } from '@/components/shared/ContextMenu';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';
import { CellEditor } from './CellEditor';
import { FilterBuilder } from './FilterBuilder';
import { ColumnManager } from './ColumnManager';
import { FilterAutocompletePopup } from './FilterAutocompletePopup';
import { SkeletonTable } from '@/components/shared/Skeleton';
import { useFilterAutocomplete, applySuggestion, type AutocompleteMode } from '@/hooks/useFilterAutocomplete';
import { qualifiedTable, quoteIdentifier } from '@/lib/sql-quote';
import { isDocumentDriver } from '@/lib/driver-kind';
import type { TableViewTab, QueryResult, SchemaMetadata, ColumnMeta, ColumnConfig, ConnectionConfig } from '@/types';

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const PAGE_SIZE = 100;

// Internal row ID field used to track rows uniquely in the grid
const ROW_ID_FIELD = '__dbgo_row_id';

type ChangeType = 'update' | 'insert' | 'delete';

interface RowChange {
  original: Record<string, unknown>;
  modified: Record<string, unknown>;
  type: ChangeType;
}

// ── Cell Selection Types ─────────────────────────────────────

interface CellPosition {
  row: number;
  col: number;
}

interface CellRange {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

function normalizeRange(range: CellRange): CellRange {
  return {
    startRow: Math.min(range.startRow, range.endRow),
    startCol: Math.min(range.startCol, range.endCol),
    endRow: Math.max(range.startRow, range.endRow),
    endCol: Math.max(range.startCol, range.endCol),
  };
}

function isCellInSelection(row: number, col: number, ranges: CellRange[]): boolean {
  return ranges.some((r) => {
    const n = normalizeRange(r);
    return row >= n.startRow && row <= n.endRow && col >= n.startCol && col <= n.endCol;
  });
}

// ── Component ────────────────────────────────────────────────

interface TableViewProps {
  tab: TableViewTab;
}

export function TableView({ tab }: TableViewProps) {
  const updateTab = useWorkspaceStore((s) => s.updateTab);
  const queryClient = useQueryClient();
  const appTheme = useAppStore((s) => s.theme);
  const gridThemeId = useSettingsStore((s) =>
    s.get(appTheme === 'dark' ? 'grid.theme.dark' : 'grid.theme.light',
          appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light'));
  const gridTheme = GRID_THEMES.find(t => t.id === gridThemeId) ?? GRID_THEMES[0];
  const gridFontFamily = useSettingsStore((s) => s.get('grid.fontFamily', "'JetBrains Mono', monospace"));
  const gridFontSize = useSettingsStore((s) => s.getNumber('grid.fontSize', 12));
  const gridRowHeight = useSettingsStore((s) => s.getNumber('grid.rowHeight', 30));
  const gridHeaderHeight = useSettingsStore((s) => s.getNumber('grid.headerHeight', 32));
  const gridCellPadding = useSettingsStore((s) => s.getNumber('grid.cellPadding', 12));
  const [whereInput, setWhereInput] = useState(tab.where);
  const [orderByInput, setOrderByInput] = useState(tab.orderBy);
  // Per-input cursor position so the autocomplete hook knows where to
  // classify "what comes next". Updated on every keyup / select; the
  // input element's selectionStart is the source of truth.
  const [whereCursor, setWhereCursor] = useState(0);
  const [orderByCursor, setOrderByCursor] = useState(0);
  // Which input is currently focused, drives popup visibility (only one
  // popup ever shows at a time). null = neither focused.
  const [activeAutocomplete, setActiveAutocomplete] = useState<AutocompleteMode | null>(null);
  // -1 = no selection yet. Enter at -1 falls through to the run-query
  // handler instead of inserting the top suggestion — the user was
  // typing a custom filter and pressing Enter to apply it. Once they
  // press ↓/↑ the index moves into the suggestion list.
  const [whereSuggestionIndex, setWhereSuggestionIndex] = useState(-1);
  const [orderBySuggestionIndex, setOrderBySuggestionIndex] = useState(-1);
  const whereInputRef = useRef<HTMLInputElement | null>(null);
  const orderByInputRef = useRef<HTMLInputElement | null>(null);
  const [page, setPage] = useState(1);
  const [filterMode, setFilterMode] = useState<'raw' | 'builder'>('raw');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(tab.columnConfig ?? []);
  const [transposed, setTransposed] = useState(tab.transposed ?? false);
  // Open `JsonViewerModal` when a chip is double-clicked. `null` = closed.
  // Stored as `{ value, title }` so the same modal serves arrays + objects.
  const [jsonViewer, setJsonViewer] = useState<{ value: unknown; title: string } | null>(null);
  const [headerContextMenu, setHeaderContextMenu] = useState<{
    position: { x: number; y: number };
    field: string;
  } | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<number, RowChange>>(new Map());
  const [gridGeneration, setGridGeneration] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const addToast = useToastStore((s) => s.addToast);
  const nextRowIdRef = useRef(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // ── Cell Selection State ─────────────────────────────────
  const [activeCell, setActiveCell] = useState<CellPosition | null>(null);
  const [selectionRanges, setSelectionRanges] = useState<CellRange[]>([]);
  const [cellContextMenu, setCellContextMenu] = useState<{
    position: { x: number; y: number };
    row: number;
    col: number;
  } | null>(null);
  const [editingCell, setEditingCell] = useState<{
    row: number;
    col: number;
    initialKey?: string;
    forceModal?: boolean;
  } | null>(null);

  // Refs for selection state (AG Grid cellClassRules read synchronously)
  const selectionRangesRef = useRef(selectionRanges);
  selectionRangesRef.current = selectionRanges;
  const activeCellRef = useRef(activeCell);
  activeCellRef.current = activeCell;
  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  // Refresh cells whenever selection or pending changes change
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [activeCell, selectionRanges, pendingChanges]);

  // Fetch schema metadata to find primary key columns.
  //
  // Both the key and the request must carry the tab's database:
  // `/api/schema/:id` without `?database=` returns only the connection's
  // *default* database, so a tab on any other database found no matching
  // table and reported "no primary key". Key shape matches ConnectionTree
  // and InfoView so all three share one cache entry.
  const { data: schemaData, isPending: schemaPending } = useQuery<SchemaMetadata>({
    queryKey: ['schema', tab.connectionId, tab.database],
    queryFn: () =>
      apiClient.get<SchemaMetadata>(
        tab.database
          ? `/api/schema/${tab.connectionId}?database=${encodeURIComponent(tab.database)}`
          : `/api/schema/${tab.connectionId}`,
      ),
    staleTime: 60_000,
  });

  // Find primary key columns for this table
  const primaryKeyColumns = useMemo<string[]>(() => {
    if (!schemaData?.databases) return [];
    for (const db of schemaData.databases) {
      for (const schema of db.schemas) {
        if (schema.name === tab.schema) {
          const table = schema.tables.find((t) => t.name === tab.table);
          if (table) {
            const pkCols = table.columns
              .filter((c: ColumnMeta) => c.isPrimaryKey)
              .map((c: ColumnMeta) => c.name);
            return pkCols;
          }
        }
      }
    }
    return [];
  }, [schemaData, tab.schema, tab.table]);

  // Driver type comes from the connections list. We subscribe via
  // useQuery (rather than `queryClient.getQueryData`) so this component
  // re-renders when the connections list arrives. On tab restore the
  // table view can mount before the connections query resolves; reading
  // the cache directly froze `driverType` at the 'postgresql' fallback,
  // which sent double-quoted identifiers to MySQL connections (causing
  // a SQL syntax error on the wrapped paged SELECT).
  const { data: connections } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
    staleTime: 60_000,
  });
  const driverType = useMemo(() => {
    return connections?.find((c) => c.id === tab.connectionId)?.driverType ?? 'postgresql';
  }, [connections, tab.connectionId]);

  const isDocDriver = isDocumentDriver(driverType);

  /**
   * Grid writes address a row by its primary key. Without one there is no
   * WHERE that provably matches a single row, so every write is blocked and
   * the user is pointed at the query editor.
   *
   * This fails closed on purpose: `primaryKeyColumns` is also empty while the
   * schema request is in flight or if it errors, so the grid starts read-only
   * and unlocks only once a PK is confirmed. The alternative — guessing an
   * identity when metadata is missing — is what turned a single-cell edit
   * into a table-wide UPDATE.
   *
   * Mongo is exempt: identity is `_id`, and `generateChangeMongosh` keeps its
   * own guard for documents that loaded without one.
   */
  const hasRowIdentity = isDocDriver || primaryKeyColumns.length > 0;
  /** Show the read-only notice only once schema resolved, so it doesn't flash
   *  on every open while the request is still in flight. */
  const showNoIdentityNotice = !hasRowIdentity && !schemaPending;

  /**
   * Translate the WHERE / ORDER BY inputs into a query the driver
   * understands. SQL drivers keep their `SELECT * FROM …` shape;
   * document drivers (Mongo) build `db.<coll>.find(<filter>).sort(<sort>)`.
   *
   * For Mongo the inputs are mongosh expressions — `{ status: "active" }`
   * for WHERE, `{ createdAt: -1 }` for ORDER BY — passed straight to the
   * driver, which already parses that shape via `executeQuery`. Empty
   * inputs mean "no filter" / "no sort".
   */
  const buildSql = useCallback(
    (where: string, orderBy: string) => {
      if (isDocDriver) {
        const filter = where.trim() || '{}';
        let mongo = `db.${tab.table}.find(${filter})`;
        if (orderBy.trim()) mongo += `.sort(${orderBy.trim()})`;
        return mongo;
      }
      let sql = `SELECT * FROM ${qualifiedTable(tab.schema, tab.table, driverType)}`;
      if (where.trim()) {
        sql += ` WHERE ${where.trim()}`;
      }
      if (orderBy.trim()) {
        sql += ` ORDER BY ${orderBy.trim()}`;
      }
      return sql;
    },
    [tab.schema, tab.table, driverType, isDocDriver],
  );

  const sql = useMemo(
    () => buildSql(tab.where, tab.orderBy),
    [buildSql, tab.where, tab.orderBy],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery<QueryResult>({
    // driverType is part of the key: on tab restore the connections list
    // can resolve after the first render, flipping driverType from
    // 'postgresql' to 'mysql' (or whatever the real driver is). Without
    // it in the key the closure-captured wrong-quoted SQL would stay
    // cached and the corrected one would never run.
    queryKey: ['table-data', tab.connectionId, tab.database, tab.schema, tab.table, tab.where, tab.orderBy, page, driverType],
    queryFn: () =>
      apiClient.post<QueryResult>('/api/query/execute', {
        connectionId: tab.connectionId,
        database: tab.database,
        sql,
        page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 30_000,
  });

  // Mark connection as connected when data loads (handles auto-connect)
  useEffect(() => {
    if (data) {
      useConnectionStore.getState().setStatus(tab.connectionId, 'connected');
    }
  }, [data, tab.connectionId]);

  // Command-bar hook: `> Export Table...` fires `dbgo:table:export`.
  // Only the active tab responds so the right grid's state is exported.
  useEffect(() => {
    function onExport() {
      if (useWorkspaceStore.getState().activeTabId === tab.id) {
        setShowExportModal(true);
      }
    }
    window.addEventListener('dbgo:table:export', onExport);
    return () => window.removeEventListener('dbgo:table:export', onExport);
  }, [tab.id]);

  // Initialize or validate columnConfig when data columns change
  useEffect(() => {
    if (!data?.columns || data.columns.length === 0) return;

    const dataFields = new Set(data.columns.map((c) => c.name));

    if (columnConfig.length === 0) {
      // No config yet — initialize with all columns visible
      setColumnConfig(data.columns.map((c) => ({ field: c.name, visible: true })));
    } else {
      // Validate existing config against actual columns
      const validConfig = columnConfig.filter((cc) => dataFields.has(cc.field));
      const hasAnyVisible = validConfig.some((cc) => cc.visible);

      if (validConfig.length === 0 || !hasAnyVisible) {
        // Config is stale or all visible columns are gone — reset to defaults
        setColumnConfig(data.columns.map((c) => ({ field: c.name, visible: true })));
      } else if (validConfig.length !== columnConfig.length) {
        // Some columns were removed from the table — clean up stale entries
        // Also add any new columns that aren't in the config yet
        const configFields = new Set(validConfig.map((cc) => cc.field));
        const newColumns = data.columns
          .filter((c) => !configFields.has(c.name))
          .map((c) => ({ field: c.name, visible: true }));
        setColumnConfig([...validConfig, ...newColumns]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.columns]);

  // Sync columnConfig and transposed to tab state
  useEffect(() => {
    updateTab(tab.id, { columnConfig, transposed } as Partial<TableViewTab>);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnConfig, transposed]);

  // Compute visible columns in display order
  const visibleColumns = useMemo(() => {
    if (!data?.columns) return [];
    if (columnConfig.length === 0) return data.columns;
    return columnConfig
      .filter((cc) => cc.visible)
      .map((cc) => data.columns.find((c) => c.name === cc.field))
      .filter((c): c is NonNullable<typeof c> => c != null);
  }, [columnConfig, data?.columns]);

  // Check if column config differs from default (all visible, original query order)
  const isColumnConfigModified = useMemo(() => {
    if (!data?.columns || columnConfig.length === 0) return false;
    if (columnConfig.length !== data.columns.length) return true;
    return columnConfig.some((cc, i) => !cc.visible || cc.field !== data.columns[i].name);
  }, [columnConfig, data?.columns]);

  // Autocomplete inputs feed the WHERE / ORDER BY popups. Columns come
  // from the latest result set (always reflects what the user can see),
  // driver type comes from the connections list cache so we can pick the
  // right operator dialect (`ILIKE` vs `REGEXP` vs `<>` vs `!=`).
  const autocompleteColumns = useMemo(
    () => (data?.columns ?? []).map((c) => ({ name: c.name, type: c.type })),
    [data?.columns],
  );
  // `driverType` is computed earlier (used by buildSql for identifier
  // quoting); the autocomplete hooks reuse it here.

  const whereAutocomplete = useFilterAutocomplete(
    whereInput, whereCursor, 'where', autocompleteColumns, driverType,
  );
  const orderByAutocomplete = useFilterAutocomplete(
    orderByInput, orderByCursor, 'orderby', autocompleteColumns, driverType,
  );

  // Row type with internal ID field
  type RowRecord = Record<string, unknown> & { [K in typeof ROW_ID_FIELD]: number };

  // Tag rows with unique IDs for change tracking
  const rowData = useMemo<RowRecord[]>(() => {
    if (!data?.rows) return [];
    const baseRows: RowRecord[] = data.rows.map((row, index) => ({
      ...row,
      [ROW_ID_FIELD]: index,
    }));
    // Track the next available ID for new rows
    nextRowIdRef.current = baseRows.length;

    // Apply pending modifications to the row data
    const rows = [...baseRows];
    // Collect inserts with their desired positions, then splice in order
    const inserts: Array<{ rowId: number; row: RowRecord; insertIndex: number }> = [];
    pendingChanges.forEach((change, rowId) => {
      if (change.type === 'update') {
        const idx = rows.findIndex((r) => r[ROW_ID_FIELD] === rowId);
        if (idx !== -1) {
          rows[idx] = { ...rows[idx], ...change.modified };
        }
      } else if (change.type === 'insert') {
        const { __dbgo_insert_index, ...rest } = change.modified;
        const insertIndex = typeof __dbgo_insert_index === 'number' ? __dbgo_insert_index : rows.length;
        inserts.push({ rowId, row: { ...rest, [ROW_ID_FIELD]: rowId } as RowRecord, insertIndex });
      }
    });
    // Insert in reverse order of index so earlier splices don't shift later positions
    inserts.sort((a, b) => b.insertIndex - a.insertIndex);
    for (const ins of inserts) {
      const idx = Math.min(ins.insertIndex, rows.length);
      rows.splice(idx, 0, ins.row);
    }

    return rows;
  }, [data?.rows, pendingChanges]);

  // Column names for filter builder
  const columnNames = useMemo<string[]>(() => {
    if (data?.columns) return data.columns.map((c) => c.name);
    return [];
  }, [data?.columns]);

  const handleFilterBuilderApply = useCallback(
    (where: string) => {
      setWhereInput(where);
      setPage(1);
      updateTab(tab.id, { where, orderBy: orderByInput });
    },
    [tab.id, orderByInput, updateTab],
  );

  const handleFilterKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setPage(1);
        updateTab(tab.id, { where: whereInput, orderBy: orderByInput });
      }
    },
    [tab.id, whereInput, orderByInput, updateTab],
  );

  // Apply a chosen suggestion back into the input + cursor + visibility
  // store. Generic over which input (where/orderby) is being completed.
  const applyAutocomplete = useCallback(
    (mode: AutocompleteMode, index: number) => {
      const ac = mode === 'where' ? whereAutocomplete : orderByAutocomplete;
      const suggestion = ac.suggestions[index];
      if (!suggestion) return;
      const inputValue = mode === 'where' ? whereInput : orderByInput;
      const setInput = mode === 'where' ? setWhereInput : setOrderByInput;
      const setCursor = mode === 'where' ? setWhereCursor : setOrderByCursor;
      const inputEl = mode === 'where' ? whereInputRef.current : orderByInputRef.current;
      const setIndex = mode === 'where' ? setWhereSuggestionIndex : setOrderBySuggestionIndex;

      const { next, cursor } = applySuggestion(inputValue, ac.replaceRange, suggestion);
      setInput(next);
      setCursor(cursor);
      // Reset to "no selection" — next ↓/↑ starts navigation again.
      setIndex(-1);
      // Restore the cursor position after React's controlled re-render.
      requestAnimationFrame(() => {
        if (inputEl) {
          inputEl.focus();
          inputEl.setSelectionRange(cursor, cursor);
        }
      });
    },
    [whereAutocomplete, orderByAutocomplete, whereInput, orderByInput],
  );

  // Keyboard handler for both filter inputs. When the popup is visible
  // and has at least one suggestion, intercept ↑↓/Enter/Tab; otherwise
  // fall through to handleFilterKeyDown's existing Enter-to-run behavior.
  const handleAutocompleteKeyDown = useCallback(
    (e: React.KeyboardEvent, mode: AutocompleteMode) => {
      const ac = mode === 'where' ? whereAutocomplete : orderByAutocomplete;
      const isPopupActive =
        activeAutocomplete === mode && ac.visible && ac.suggestions.length > 0;
      const selectedIndex = mode === 'where' ? whereSuggestionIndex : orderBySuggestionIndex;
      const setSelectedIndex = mode === 'where' ? setWhereSuggestionIndex : setOrderBySuggestionIndex;

      if (isPopupActive) {
        // ↓: -1 → 0; else wrap forward. ↑: -1 → last; else wrap back.
        // The -1 sentinel means "no row selected" — that's the default
        // until the user actively starts navigating.
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(selectedIndex < 0 ? 0 : (selectedIndex + 1) % ac.suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(
            selectedIndex < 0
              ? ac.suggestions.length - 1
              : (selectedIndex - 1 + ac.suggestions.length) % ac.suggestions.length,
          );
          return;
        }
        if (e.key === 'Tab') {
          // Tab is the explicit "complete with top suggestion" key —
          // selecting the first row even when nothing's highlighted
          // matches what editors do.
          e.preventDefault();
          applyAutocomplete(mode, selectedIndex < 0 ? 0 : selectedIndex);
          return;
        }
        if (e.key === 'Enter') {
          // Enter applies a suggestion *only* when one is highlighted.
          // With no selection, fall through so the user's typed value
          // runs as-is — the most common case is "I typed what I want,
          // please run it." Either way, dismiss the popup: applying a
          // suggestion or running the query both end the typing flow.
          if (selectedIndex >= 0) {
            e.preventDefault();
            applyAutocomplete(mode, selectedIndex);
          }
          setActiveAutocomplete(null);
          setSelectedIndex(-1);
          if (selectedIndex >= 0) return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setActiveAutocomplete(null);
          setSelectedIndex(-1);
          return;
        }
      }

      // Fall through to the existing Enter-runs-the-query handler.
      handleFilterKeyDown(e);
    },
    [whereAutocomplete, orderByAutocomplete, activeAutocomplete,
     whereSuggestionIndex, orderBySuggestionIndex,
     applyAutocomplete, handleFilterKeyDown],
  );

  // Track cursor / selection on input changes, focus, click, key events.
  const updateCursor = useCallback((mode: AutocompleteMode, el: HTMLInputElement) => {
    const pos = el.selectionStart ?? el.value.length;
    if (mode === 'where') setWhereCursor(pos);
    else setOrderByCursor(pos);
  }, []);

  // Determine which columns have been modified for a given row
  const getModifiedColumns = useCallback(
    (rowId: number): Set<string> => {
      const change = pendingChangesRef.current.get(rowId);
      if (!change) return new Set();
      if (change.type === 'insert') {
        return new Set(Object.keys(change.modified));
      }
      if (change.type === 'update') {
        const modified = new Set<string>();
        for (const key of Object.keys(change.modified)) {
          if (change.original[key] !== change.modified[key]) {
            modified.add(key);
          }
        }
        return modified;
      }
      return new Set();
    },
    [],
  );

  // ── Pending Change Tracking ────────────────────────────────

  /** Record a single cell value change as a pending change */
  const trackCellChange = useCallback(
    (rowIndex: number, colName: string, newValue: unknown) => {
      if (!hasRowIdentity) {
        useToastStore.getState().addToast(
          'warning',
          'Read-only: no primary key on this table — use the query editor to change data',
        );
        return;
      }
      // Editing _id is silently ignored on Mongo — the document
      // identity is its primary key. Other Mongo edits flow through
      // the SQL path; `generateChangeMongosh` translates them.
      if (isDocDriver && colName === '_id') {
        useToastStore.getState().addToast('warning', 'Cannot edit _id on a MongoDB document');
        return;
      }
      const row = rowData[rowIndex];
      if (!row) return;
      const rowId = row[ROW_ID_FIELD] as number;

      // Skip if value hasn't actually changed
      const currentValue = row[colName];
      if (currentValue === newValue) return;
      // Also handle null/undefined equivalence
      if ((currentValue === null || currentValue === undefined) &&
          (newValue === null || newValue === undefined)) return;

      setPendingChanges((prev) => {
        const next = new Map(prev);
        const existing = next.get(rowId);

        if (existing && existing.type === 'insert') {
          next.set(rowId, {
            ...existing,
            modified: { ...existing.modified, [colName]: newValue },
          });
        } else if (existing && existing.type === 'update') {
          const newModified = { ...existing.modified, [colName]: newValue };
          const allReverted = Object.keys(newModified).every(
            (key) => newModified[key] === existing.original[key],
          );
          if (allReverted) {
            next.delete(rowId);
          } else {
            next.set(rowId, { ...existing, modified: newModified });
          }
        } else {
          // New update change — capture the full original row
          const originalRow: Record<string, unknown> = {};
          if (data?.columns) {
            for (const col of data.columns) {
              originalRow[col.name] = row[col.name];
            }
          }
          next.set(rowId, {
            original: originalRow,
            modified: { [colName]: newValue },
            type: 'update',
          });
        }

        return next;
      });
    },
    [data?.columns, rowData, isDocDriver, hasRowIdentity],
  );

  const handleAddRow = useCallback((position?: 'before' | 'after') => {
    // An INSERT needs no row identity, but a row added to a table the grid
    // cannot subsequently address is a trap — it could never be edited or
    // deleted from here. Keep the whole grid read-only instead.
    if (!hasRowIdentity) {
      useToastStore.getState().addToast(
        'warning',
        'Read-only: no primary key on this table — use the query editor to insert rows',
      );
      return;
    }
    const newRowId = nextRowIdRef.current++;
    const emptyRow: Record<string, unknown> = {};
    if (data?.columns) {
      for (const col of data.columns) {
        emptyRow[col.name] = null;
      }
    }

    // Determine insert index relative to active cell or selection
    let insertIndex = rowData.length; // default: append
    if (position && activeCell) {
      insertIndex = position === 'before' ? activeCell.row : activeCell.row + 1;
    }

    // Store the desired insert position in the row metadata
    emptyRow['__dbgo_insert_index'] = insertIndex;

    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.set(newRowId, {
        original: {},
        modified: emptyRow,
        type: 'insert',
      });
      return next;
    });

    // Scroll to and select the new row after React re-renders
    setTimeout(() => {
      const api = gridApiRef.current;
      if (!api) return;
      const targetIndex = position && activeCell
        ? (position === 'before' ? activeCell.row : activeCell.row + 1)
        : api.getDisplayedRowCount() - 1;
      api.ensureIndexVisible(targetIndex, 'middle');
      setActiveCell({ row: targetIndex, col: 0 });
      setSelectionRanges([{
        startRow: targetIndex, startCol: 0,
        endRow: targetIndex, endCol: (data?.columns?.length ?? 1) - 1,
      }]);
    }, 50);
  }, [data?.columns, rowData.length, activeCell, isDocDriver, hasRowIdentity]);

  const handleDeleteRow = useCallback(() => {
    if (!hasRowIdentity) {
      useToastStore.getState().addToast(
        'warning',
        'Read-only: no primary key on this table — use the query editor to delete rows',
      );
      return;
    }
    // If we have active cell selection, delete those rows
    if (selectionRanges.length > 0 && rowData.length > 0 && data?.columns) {
      setPendingChanges((prev) => {
        const next = new Map(prev);
        const rowSet = new Set<number>();
        for (const range of selectionRanges) {
          const n = normalizeRange(range);
          for (let r = n.startRow; r <= n.endRow; r++) {
            rowSet.add(r);
          }
        }
        for (const ri of rowSet) {
          const row = rowData[ri];
          if (!row) continue;
          const rowId = row[ROW_ID_FIELD] as number;
          const existing = next.get(rowId);

          if (existing?.type === 'insert') {
            next.delete(rowId);
          } else if (existing?.type === 'delete') {
            next.delete(rowId);
          } else {
            const originalRow: Record<string, unknown> = {};
            for (const col of data.columns) {
              originalRow[col.name] = row[col.name];
            }
            next.set(rowId, {
              original: originalRow,
              modified: originalRow,
              type: 'delete',
            });
          }
        }
        return next;
      });
      return;
    }

    // Fallback to AG Grid row selection
    if (!gridApiRef.current) return;
    const selectedRows = gridApiRef.current.getSelectedRows() as Record<string, unknown>[];
    if (selectedRows.length === 0) return;

    setPendingChanges((prev) => {
      const next = new Map(prev);
      for (const row of selectedRows) {
        const rowId = row[ROW_ID_FIELD] as number;
        const existing = next.get(rowId);

        if (existing?.type === 'insert') {
          next.delete(rowId);
        } else if (existing?.type === 'delete') {
          next.delete(rowId);
        } else {
          const originalRow: Record<string, unknown> = {};
          if (data?.columns) {
            for (const col of data.columns) {
              originalRow[col.name] = row[col.name];
            }
          }
          next.set(rowId, {
            original: originalRow,
            modified: originalRow,
            type: 'delete',
          });
        }
      }
      return next;
    });
  }, [data?.columns, rowData, selectionRanges, isDocDriver, hasRowIdentity]);

  const handleRevert = useCallback(() => {
    setPendingChanges(new Map());
    setApplyError(null);
    setGridGeneration((g) => g + 1);
  }, []);

  // Build a SQL identifier using the connection's dialect quoting.
  // MySQL needs backticks, MSSQL needs brackets; ANSI double-quotes
  // only work on MySQL when ANSI_QUOTES mode is on (off by default).
  const quoteIdent = useCallback(
    (name: string): string => quoteIdentifier(name, driverType),
    [driverType],
  );

  // Build a SQL literal from a JS value
  const sqlLiteral = useCallback((value: unknown): string => {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
    if (typeof value === 'object') return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
    return `'${String(value).replace(/'/g, "''")}'`;
  }, []);

  /**
   * WHERE clause targeting exactly the row `row` came from, or null when the
   * table has no primary key to address it by.
   *
   * There is deliberately no fallback. This previously used the first column
   * when no PK was known, which is not an identity: on a table whose first
   * column was a low-cardinality flag, a one-cell edit generated
   * `UPDATE … WHERE first_source = 'MOBILEAPP'` and rewrote every matching
   * row. Callers must treat null as "cannot address this row" and refuse.
   */
  const buildWhereClause = useCallback(
    (row: Record<string, unknown>): string | null => {
      if (primaryKeyColumns.length === 0) return null;
      const conditions = primaryKeyColumns.map(
        (col) => `${quoteIdent(col)} = ${sqlLiteral(row[col])}`,
      );
      return conditions.join(' AND ');
    },
    [primaryKeyColumns, quoteIdent, sqlLiteral],
  );

  const generateChangeSQL = useCallback((): string[] => {
    const statements: string[] = [];
    const tableRef = qualifiedTable(tab.schema, tab.table, driverType);

    pendingChanges.forEach((change) => {
      if (change.type === 'update') {
        const setClauses: string[] = [];
        for (const key of Object.keys(change.modified)) {
          if (change.original[key] !== change.modified[key]) {
            setClauses.push(`${quoteIdent(key)} = ${sqlLiteral(change.modified[key])}`);
          }
        }
        if (setClauses.length > 0) {
          const where = buildWhereClause(change.original);
          // No PK — emit nothing. handleApply refuses before reaching here;
          // this is the last line of defence against an unbounded UPDATE.
          if (where === null) return;
          statements.push(`UPDATE ${tableRef} SET ${setClauses.join(', ')} WHERE ${where}`);
        }
      } else if (change.type === 'insert') {
        const cols: string[] = [];
        const vals: string[] = [];
        for (const [key, value] of Object.entries(change.modified)) {
          // Skip our own row bookkeeping. `handleAddRow` stamps
          // `__dbgo_insert_index` onto the pending row and the grid keys off
          // `__dbgo_row_id`; neither is a real column. Leaking them produced
          // `Unknown column '__dbgo_insert_index' in 'field list'`, which
          // failed the statement *after* BEGIN and left the pooled connection
          // inside an open transaction. `generateChangeMongosh` has always
          // filtered these — the SQL path never did.
          if (key.startsWith('__dbgo')) continue;
          cols.push(quoteIdent(key));
          vals.push(sqlLiteral(value));
        }
        if (cols.length > 0) {
          statements.push(`INSERT INTO ${tableRef} (${cols.join(', ')}) VALUES (${vals.join(', ')})`);
        }
      } else if (change.type === 'delete') {
        const where = buildWhereClause(change.original);
        if (where === null) return;
        statements.push(`DELETE FROM ${tableRef} WHERE ${where}`);
      }
    });

    return statements;
  }, [pendingChanges, tab, driverType, quoteIdent, sqlLiteral, buildWhereClause]);

  /** Render a JS value as mongosh source. Wraps 24-hex strings in
   *  `_id` columns as `ObjectId(…)`; everything else round-trips
   *  through `JSON.stringify`. Dates become `ISODate(…)`. */
  const mongoLiteral = useCallback((value: unknown, fieldName: string): string => {
    if (value === null || value === undefined) return 'null';
    if (value instanceof Date) return `ISODate(${JSON.stringify(value.toISOString())})`;
    if (typeof value === 'string') {
      // _id strings that look like ObjectId hex round-trip as ObjectId so
      // updateOne / deleteOne filters match. Anything else stays string.
      if (fieldName === '_id' && /^[0-9a-fA-F]{24}$/.test(value)) {
        return `ObjectId(${JSON.stringify(value)})`;
      }
      return JSON.stringify(value);
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
  }, []);

  /** Build mongosh write ops from pendingChanges. Update + delete key
   *  off the document's `_id`; rows without `_id` are skipped because
   *  there's no safe filter to target them. Insert omits any `__dbgo*`
   *  internal fields. */
  const generateChangeMongosh = useCallback((): string[] => {
    const statements: string[] = [];
    const coll = tab.table;

    pendingChanges.forEach((change) => {
      if (change.type === 'update') {
        const setFields: string[] = [];
        for (const key of Object.keys(change.modified)) {
          if (key === '_id' || key.startsWith('__dbgo')) continue;
          if (change.original[key] === change.modified[key]) continue;
          setFields.push(`${JSON.stringify(key)}: ${mongoLiteral(change.modified[key], key)}`);
        }
        if (setFields.length === 0) return;
        const id = change.original['_id'];
        if (id === undefined || id === null) {
          // Without _id we'd have to filter on the full document — too
          // dangerous. Surface to the user via toast at apply time.
          return;
        }
        statements.push(
          `db.${coll}.updateOne({_id: ${mongoLiteral(id, '_id')}}, {$set: {${setFields.join(', ')}}})`,
        );
      } else if (change.type === 'insert') {
        const fields: string[] = [];
        for (const [key, value] of Object.entries(change.modified)) {
          if (key.startsWith('__dbgo')) continue;
          if (value === null) continue; // omit null fields for a cleaner insert
          fields.push(`${JSON.stringify(key)}: ${mongoLiteral(value, key)}`);
        }
        statements.push(`db.${coll}.insertOne({${fields.join(', ')}})`);
      } else if (change.type === 'delete') {
        const id = change.original['_id'];
        if (id === undefined || id === null) return;
        statements.push(`db.${coll}.deleteOne({_id: ${mongoLiteral(id, '_id')}})`);
      }
    });

    return statements;
  }, [pendingChanges, tab.table, mongoLiteral]);

  const handleApply = useCallback(async () => {
    // Backstop. The UI blocks editing without a primary key, but no write may
    // ever reach the server without one: an UPDATE that cannot name its row
    // is the failure that rewrote an entire table.
    if (!hasRowIdentity) {
      setApplyError(
        'This table has no primary key, so DBGo cannot target individual rows. Use the query editor with an explicit WHERE.',
      );
      return;
    }
    // Document drivers (Mongo) — loop statement-by-statement, no
    // transactions. A failure halts the loop and reports how many
    // succeeded before; the rest stay pending so the user can review.
    if (isDocDriver) {
      const statements = generateChangeMongosh();
      if (statements.length === 0) {
        if (pendingChanges.size > 0) {
          setApplyError('No applicable changes (rows without _id are skipped). Edit only documents that loaded with _id.');
        }
        return;
      }

      setIsApplying(true);
      setApplyError(null);
      let succeeded = 0;
      try {
        for (const stmt of statements) {
          await apiClient.post('/api/query/execute', {
            connectionId: tab.connectionId,
            database: tab.database,
            sql: stmt,
          });
          succeeded++;
        }
        setPendingChanges(new Map());
        setShowReviewModal(false);
        setGridGeneration((g) => g + 1);
        await queryClient.invalidateQueries({
          queryKey: ['table-data', tab.connectionId, tab.database, tab.schema, tab.table],
        });
        addToast('success', `Applied ${succeeded} change${succeeded !== 1 ? 's' : ''}`);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to apply changes';
        setApplyError(`${message} (${succeeded}/${statements.length} succeeded before failure)`);
      } finally {
        setIsApplying(false);
      }
      return;
    }

    const statements = generateChangeSQL();
    if (statements.length === 0) return;

    const sql = `BEGIN;\n${statements.map((s) => `${s};`).join('\n')}\nCOMMIT;`;

    setIsApplying(true);
    setApplyError(null);

    try {
      await apiClient.post('/api/query/execute', {
        connectionId: tab.connectionId,
        database: tab.database,
        sql,
      });

      setPendingChanges(new Map());
      setShowReviewModal(false);
      setGridGeneration((g) => g + 1);
      await queryClient.invalidateQueries({
        queryKey: ['table-data', tab.connectionId, tab.database, tab.schema, tab.table],
      });
      addToast('success', `Applied ${statements.length} change${statements.length !== 1 ? 's' : ''} in transaction`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to apply changes';
      setApplyError(message);
    } finally {
      setIsApplying(false);
    }
  }, [generateChangeSQL, generateChangeMongosh, isDocDriver, hasRowIdentity, pendingChanges.size, tab.connectionId, tab.database, tab.schema, tab.table, queryClient, addToast]);

  const pendingChangeCount = pendingChanges.size;

  // Warn on page unload when there are unsaved changes
  useEffect(() => {
    if (pendingChangeCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingChangeCount]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  // Sync AG Grid column order back to columnConfig after drag reorder
  const onDragStopped = useCallback((_event: DragStoppedEvent) => {
    const api = gridApiRef.current;
    if (!api) return;
    const gridColState = api.getColumnState();
    // Get ordered field names from AG Grid (skip internal columns)
    const orderedFields = gridColState
      .map((c) => c.colId)
      .filter((id) => id !== '__dbgo_row_num' && id !== '__field');

    if (orderedFields.length === 0) return;

    setColumnConfig((prev) => {
      // Build a map of existing config
      const configMap = new Map(prev.map((cc) => [cc.field, cc]));
      // Rebuild config in the new order
      const newConfig: ColumnConfig[] = orderedFields.map((field) => {
        const existing = configMap.get(field);
        return existing ?? { field, visible: true };
      });
      // Append any columns that weren't in the grid state (hidden ones)
      for (const cc of prev) {
        if (!orderedFields.includes(cc.field)) {
          newConfig.push(cc);
        }
      }
      return newConfig;
    });
  }, []);

  // ── Suppress browser context menu on entire grid + header context menu ──
  useEffect(() => {
    if (!gridContainerRef.current) return;
    const handler = (e: MouseEvent) => {
      // Always prevent the browser default context menu inside the grid
      e.preventDefault();

      // If right-clicking a header cell, show our header context menu
      // and stop propagation so AG Grid doesn't also handle it
      const headerCell = (e.target as HTMLElement).closest('.ag-header-cell');
      if (headerCell) {
        e.stopPropagation();
        const colId = headerCell.getAttribute('col-id');
        if (!colId || colId === '__dbgo_row_num') return;
        setHeaderContextMenu({
          position: { x: e.clientX, y: e.clientY },
          field: colId,
        });
      }
      // Cell right-click: let the event propagate to AG Grid's onCellContextMenu
    };
    const container = gridContainerRef.current;
    container.addEventListener('contextmenu', handler, true);
    return () => container.removeEventListener('contextmenu', handler, true);
  }, []);

  const handleHideColumn = useCallback((field: string) => {
    setColumnConfig((prev) => {
      const visCount = prev.filter((cc) => cc.visible).length;
      if (visCount <= 1) return prev; // Keep at least one visible
      return prev.map((cc) => cc.field === field ? { ...cc, visible: false } : cc);
    });
    setGridGeneration((g) => g + 1);
  }, []);

  const handleSortColumn = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    const orderBy = `"${field}" ${direction}`;
    setOrderByInput(orderBy);
    setPage(1);
    updateTab(tab.id, { orderBy, where: whereInput });
  }, [tab.id, whereInput, updateTab]);

  const headerContextMenuItems = useMemo<ContextMenuItem[]>(() => {
    if (!headerContextMenu) return [];
    const { field } = headerContextMenu;
    const colState = gridApiRef.current?.getColumnState();
    const currentColState = colState?.find((c) => c.colId === field);
    const isPinned = currentColState?.pinned != null && currentColState.pinned !== false;

    return [
      {
        label: 'Hide Column',
        icon: EyeOff,
        onClick: () => handleHideColumn(field),
      },
      {
        label: 'Pin Left',
        icon: PinIcon,
        onClick: () => gridApiRef.current?.applyColumnState({ state: [{ colId: field, pinned: 'left' }] }),
        disabled: currentColState?.pinned === 'left',
      },
      {
        label: 'Pin Right',
        icon: PinIcon,
        onClick: () => gridApiRef.current?.applyColumnState({ state: [{ colId: field, pinned: 'right' }] }),
        disabled: currentColState?.pinned === 'right',
      },
      ...(isPinned
        ? [{
            label: 'Unpin',
            icon: PinIcon,
            onClick: () => gridApiRef.current?.applyColumnState({ state: [{ colId: field, pinned: null }] }),
          }]
        : []),
      { label: '', separator: true },
      {
        label: 'Auto-size Column',
        icon: Maximize2,
        onClick: () => gridApiRef.current?.autoSizeColumns([field]),
      },
      {
        label: 'Auto-size All Columns',
        icon: Maximize2,
        onClick: () => {
          const allColIds = gridApiRef.current?.getColumns()?.map((c) => c.getColId()) ?? [];
          gridApiRef.current?.autoSizeColumns(allColIds);
        },
      },
      { label: '', separator: true },
      {
        label: 'Sort Ascending',
        icon: ArrowUp,
        onClick: () => handleSortColumn(field, 'ASC'),
      },
      {
        label: 'Sort Descending',
        icon: ArrowDown,
        onClick: () => handleSortColumn(field, 'DESC'),
      },
    ];
  }, [headerContextMenu, handleHideColumn, handleSortColumn]);

  // Column manager config change handler
  const handleColumnConfigChange = useCallback((newConfig: ColumnConfig[]) => {
    setColumnConfig(newConfig);
    setGridGeneration((g) => g + 1);
  }, []);

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const api = event.api;
      const sortedCols = api
        .getColumnState()
        .filter((c) => c.sort != null);

      if (sortedCols.length === 0) {
        setOrderByInput('');
        setPage(1);
        updateTab(tab.id, { orderBy: '', where: whereInput });
        return;
      }

      const orderBy = sortedCols
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((c) => `"${c.colId}" ${c.sort === 'asc' ? 'ASC' : 'DESC'}`)
        .join(', ');

      setOrderByInput(orderBy);
      setPage(1);
      updateTab(tab.id, { orderBy, where: whereInput });
    },
    [tab.id, whereInput, updateTab],
  );

  // ── Cell Selection Handlers ────────────────────────────────

  const handleCellClick = useCallback((event: CellClickedEvent) => {
    const rowIndex = event.rowIndex ?? 0;
    const colIndex = data?.columns.findIndex((c) => c.name === event.colDef.field) ?? 0;
    const nativeEvent = event.event as MouseEvent | undefined;

    if (nativeEvent?.shiftKey && activeCell) {
      // Shift+Click: extend selection from anchor to here
      setSelectionRanges([{
        startRow: activeCell.row,
        startCol: activeCell.col,
        endRow: rowIndex,
        endCol: colIndex,
      }]);
    } else if (nativeEvent?.metaKey || nativeEvent?.ctrlKey) {
      // Cmd/Ctrl+Click: add cell to selection
      const isSelected = isCellInSelection(rowIndex, colIndex, selectionRanges);
      if (isSelected) {
        // Reset to just this cell
        setSelectionRanges([{ startRow: rowIndex, startCol: colIndex, endRow: rowIndex, endCol: colIndex }]);
      } else {
        setSelectionRanges((prev) => [
          ...prev,
          { startRow: rowIndex, startCol: colIndex, endRow: rowIndex, endCol: colIndex },
        ]);
      }
      setActiveCell({ row: rowIndex, col: colIndex });
    } else {
      // Normal click: select just this cell
      setActiveCell({ row: rowIndex, col: colIndex });
      setSelectionRanges([{ startRow: rowIndex, startCol: colIndex, endRow: rowIndex, endCol: colIndex }]);
    }
  }, [activeCell, selectionRanges, data?.columns]);

  const openEditor = useCallback((row: number, col: number, initialKey?: string, forceModal?: boolean) => {
    setEditingCell({ row, col, initialKey, forceModal });
  }, []);

  const handleCellDoubleClick = useCallback((event: CellDoubleClickedEvent) => {
    const rowIndex = event.rowIndex ?? 0;
    const colIndex = data?.columns.findIndex((c) => c.name === event.colDef.field) ?? 0;
    // Skip nested values — CellEditor only handles primitives. Nested
    // cells use the JsonChipRenderer's own double-click → JSON viewer.
    // Double-clicking the whitespace around a chip also lands here, so
    // the guard prevents a phantom edit-then-commit pending change.
    if (event.value !== null && event.value !== undefined && typeof event.value === 'object') {
      const title = String(event.colDef.field ?? 'value');
      setJsonViewer({ value: event.value, title });
      return;
    }
    openEditor(rowIndex, colIndex);
  }, [data?.columns, openEditor]);

  const handleCellContextMenu = useCallback((event: CellContextMenuEvent) => {
    event.event?.preventDefault();
    const rowIndex = event.rowIndex ?? 0;
    const colIndex = data?.columns.findIndex((c) => c.name === event.colDef.field) ?? 0;

    // If right-clicked cell is not in current selection, select it
    if (!isCellInSelection(rowIndex, colIndex, selectionRanges)) {
      setActiveCell({ row: rowIndex, col: colIndex });
      setSelectionRanges([{ startRow: rowIndex, startCol: colIndex, endRow: rowIndex, endCol: colIndex }]);
    }

    const nativeEvent = event.event as MouseEvent;
    setCellContextMenu({
      position: { x: nativeEvent.clientX, y: nativeEvent.clientY },
      row: rowIndex,
      col: colIndex,
    });
  }, [data?.columns, selectionRanges]);

  // ── Clipboard & Cell Operations ────────────────────────────

  const copySelection = useCallback(() => {
    if (!data?.columns || !rowData || selectionRanges.length === 0) return;

    const range = normalizeRange(selectionRanges[0]);
    const lines: string[] = [];

    for (let r = range.startRow; r <= range.endRow; r++) {
      const row = rowData[r];
      if (!row) continue;
      const cells: string[] = [];
      for (let c = range.startCol; c <= range.endCol; c++) {
        const col = data.columns[c];
        if (!col) continue;
        const val = row[col.name];
        cells.push(val === null || val === undefined ? '' : String(val));
      }
      lines.push(cells.join('\t'));
    }

    navigator.clipboard.writeText(lines.join('\n')).catch(() => {
      // Clipboard write failed (permissions)
    });
  }, [data?.columns, rowData, selectionRanges]);

  const setSelectedCellsToNull = useCallback(() => {
    if (!data?.columns || !rowData || selectionRanges.length === 0) return;

    for (const range of selectionRanges) {
      const n = normalizeRange(range);
      for (let r = n.startRow; r <= n.endRow; r++) {
        for (let c = n.startCol; c <= n.endCol; c++) {
          const col = data.columns[c];
          if (!col) continue;
          trackCellChange(r, col.name, null);
        }
      }
    }

    gridApiRef.current?.refreshCells({ force: true });
  }, [data?.columns, rowData, selectionRanges, trackCellChange]);

  const pasteFromClipboard = useCallback(async () => {
    if (!activeCell || !data?.columns || !rowData) return;

    try {
      const text = await navigator.clipboard.readText();
      const lines = text.split('\n').map((line) => line.split('\t'));

      for (let r = 0; r < lines.length; r++) {
        const targetRow = activeCell.row + r;
        if (targetRow >= rowData.length) break;

        for (let c = 0; c < lines[r].length; c++) {
          const targetCol = activeCell.col + c;
          if (targetCol >= data.columns.length) break;

          const colName = data.columns[targetCol].name;
          const pastedValue = lines[r][c];
          trackCellChange(targetRow, colName, pastedValue === '' ? null : pastedValue);
        }
      }

      gridApiRef.current?.refreshCells({ force: true });
    } catch {
      // Clipboard read failed (permissions)
    }
  }, [activeCell, data?.columns, rowData, trackCellChange]);

  // ── Cell Editor Callbacks ──────────────────────────────────

  const handleEditorSave = useCallback((value: unknown) => {
    if (!editingCell || !data?.columns) return;
    const col = data.columns[editingCell.col];
    if (!col) return;

    trackCellChange(editingCell.row, col.name, value);
    setEditingCell(null);
    gridApiRef.current?.refreshCells({ force: true });
  }, [editingCell, data?.columns, trackCellChange]);

  const handleEditorCancel = useCallback(() => {
    setEditingCell(null);
  }, []);

  const handleEditorSaveAndMove = useCallback((value: unknown, direction: 'down' | 'right') => {
    if (!editingCell || !data?.columns) return;
    const col = data.columns[editingCell.col];
    if (!col) return;

    trackCellChange(editingCell.row, col.name, value);
    setEditingCell(null);

    const maxRow = (rowData?.length ?? 1) - 1;
    const maxCol = data.columns.length - 1;

    let newRow = editingCell.row;
    let newCol = editingCell.col;

    if (direction === 'down') {
      newRow = Math.min(maxRow, editingCell.row + 1);
    } else {
      newCol = Math.min(maxCol, editingCell.col + 1);
    }

    setActiveCell({ row: newRow, col: newCol });
    setSelectionRanges([{ startRow: newRow, startCol: newCol, endRow: newRow, endCol: newCol }]);
    gridApiRef.current?.refreshCells({ force: true });
  }, [editingCell, data?.columns, rowData, trackCellChange]);

  // Get cell DOM rect for positioning the editor
  const getEditingCellRect = useCallback((): DOMRect | null => {
    if (!editingCell || !data?.columns) return null;
    const col = data.columns[editingCell.col];
    if (!col) return null;

    const cellEl = gridContainerRef.current?.querySelector(
      `[row-index="${editingCell.row}"] [col-id="${col.name}"]`,
    );
    return cellEl?.getBoundingClientRect() ?? null;
  }, [editingCell, data?.columns]);

  // ── Keyboard Navigation ────────────────────────────────────

  const handleGridKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Don't handle keys when editing
    if (editingCell) return;
    if (!activeCell || !data?.columns) return;

    const maxRow = (rowData?.length ?? 1) - 1;
    const maxCol = data.columns.length - 1;

    // Arrow keys
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const deltas: Record<string, [number, number]> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const delta = deltas[e.key];

      if (e.shiftKey) {
        // Extend selection from the current range end (not from activeCell)
        const lastRange = selectionRanges[selectionRanges.length - 1] ?? {
          startRow: activeCell.row,
          startCol: activeCell.col,
          endRow: activeCell.row,
          endCol: activeCell.col,
        };
        const extendRow = Math.max(0, Math.min(maxRow, lastRange.endRow + delta[0]));
        const extendCol = Math.max(0, Math.min(maxCol, lastRange.endCol + delta[1]));
        setSelectionRanges([
          ...selectionRanges.slice(0, -1),
          { ...lastRange, endRow: extendRow, endCol: extendCol },
        ]);
      } else {
        const newRow = Math.max(0, Math.min(maxRow, activeCell.row + delta[0]));
        const newCol = Math.max(0, Math.min(maxCol, activeCell.col + delta[1]));
        setActiveCell({ row: newRow, col: newCol });
        setSelectionRanges([{ startRow: newRow, startCol: newCol, endRow: newRow, endCol: newCol }]);
      }
      return;
    }

    // Ctrl+A: select all
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      e.preventDefault();
      setSelectionRanges([{ startRow: 0, startCol: 0, endRow: maxRow, endCol: maxCol }]);
      return;
    }

    // Escape: clear selection
    if (e.key === 'Escape') {
      setSelectionRanges([]);
      setActiveCell(null);
      return;
    }

    // F2: edit active cell
    if (e.key === 'F2') {
      e.preventDefault();
      openEditor(activeCell.row, activeCell.col);
      return;
    }

    // Delete/Backspace: set selected cells to NULL
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      setSelectedCellsToNull();
      return;
    }

    // Ctrl+C: copy
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      copySelection();
      return;
    }

    // Ctrl+V: paste
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      pasteFromClipboard();
      return;
    }

    // Ctrl+X: cut
    if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
      e.preventDefault();
      copySelection();
      setSelectedCellsToNull();
      return;
    }

    // Typing starts editing (printable characters)
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      openEditor(activeCell.row, activeCell.col, e.key);
      return;
    }
  }, [
    activeCell, selectionRanges, data?.columns, rowData, editingCell,
    openEditor, copySelection, pasteFromClipboard, setSelectedCellsToNull,
  ]);

  // ── Context Menu Items ─────────────────────────────────────

  const contextMenuItems = useMemo<ContextMenuItem[]>(() => {
    if (!cellContextMenu) return [];

    // Check if exactly 1 cell is selected
    const singleCell = selectionRanges.length === 1 && (() => {
      const n = normalizeRange(selectionRanges[0]);
      return n.startRow === n.endRow && n.startCol === n.endCol;
    })();

    return [
      {
        label: 'Edit',
        icon: Pencil,
        onClick: () => openEditor(cellContextMenu.row, cellContextMenu.col),
        disabled: !singleCell || !hasRowIdentity,
      },
      {
        label: 'Edit in Modal',
        icon: Maximize2,
        onClick: () => openEditor(cellContextMenu.row, cellContextMenu.col, undefined, true),
        disabled: !singleCell || !hasRowIdentity,
      },
      {
        label: 'Set NULL',
        icon: CircleSlash,
        onClick: () => setSelectedCellsToNull(),
        disabled: !hasRowIdentity,
      },
      { label: '', separator: true },
      {
        label: 'Copy',
        icon: Copy,
        onClick: () => copySelection(),
      },
      {
        label: 'Paste',
        icon: ClipboardPaste,
        onClick: () => { pasteFromClipboard(); },
        disabled: !hasRowIdentity,
      },
      {
        label: 'Cut',
        icon: Scissors,
        onClick: () => {
          copySelection();
          setSelectedCellsToNull();
        },
        disabled: !hasRowIdentity,
      },
      { label: '', separator: true },
      {
        label: 'Insert Row Before',
        icon: Plus,
        onClick: () => handleAddRow('before'),
        disabled: !hasRowIdentity,
      },
      {
        label: 'Insert Row After',
        icon: Plus,
        onClick: () => handleAddRow('after'),
        disabled: !hasRowIdentity,
      },
      {
        label: 'Delete Row(s)',
        icon: Trash2,
        danger: true,
        onClick: handleDeleteRow,
        disabled: !hasRowIdentity,
      },
    ];
  }, [
    cellContextMenu, selectionRanges, openEditor, hasRowIdentity,
    setSelectedCellsToNull, copySelection, pasteFromClipboard,
    handleAddRow, handleDeleteRow,
  ]);

  // ── Column Defs with Selection Styling ─────────────────────

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!data?.columns) return [];

    const rowNumCol: ColDef = {
      colId: '__dbgo_row_num',
      headerName: '#',
      valueGetter: (params) => params.node?.rowIndex != null ? params.node.rowIndex + 1 : '',
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      resizable: false,
      sortable: false,
      editable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      cellStyle: {
        color: 'var(--text-muted)',
        fontSize: '11px',
        textAlign: 'right',
        paddingRight: '8px',
        cursor: 'pointer',
        userSelect: 'none',
      },
      cellClass: 'dbgo-row-number',
      onCellClicked: (event: CellClickedEvent) => {
        const rowIndex = event.rowIndex ?? 0;
        const maxCol = (data?.columns?.length ?? 1) - 1;
        const nativeEvent = event.event as MouseEvent | undefined;

        if (nativeEvent?.shiftKey && activeCellRef.current) {
          // Shift+Click: extend from anchor row to this row (full width)
          setSelectionRanges([{
            startRow: activeCellRef.current.row,
            startCol: 0,
            endRow: rowIndex,
            endCol: maxCol,
          }]);
        } else if (nativeEvent?.metaKey || nativeEvent?.ctrlKey) {
          // Cmd+Click: add this row to selection
          setSelectionRanges(prev => [...prev, {
            startRow: rowIndex, startCol: 0,
            endRow: rowIndex, endCol: maxCol,
          }]);
          setActiveCell({ row: rowIndex, col: 0 });
        } else {
          // Normal click: select entire row
          setActiveCell({ row: rowIndex, col: 0 });
          setSelectionRanges([{
            startRow: rowIndex, startCol: 0,
            endRow: rowIndex, endCol: maxCol,
          }]);
        }
        gridApiRef.current?.refreshCells({ force: true });
      },
    };

    const dataCols = data.columns.map((col, colIndex): ColDef => ({
      field: col.name,
      headerName: col.name,
      resizable: true,
      sortable: true,
      editable: false, // We handle editing ourselves
      minWidth: 80,
      cellClassRules: {
        'dbgo-cell-selected': (params: CellClassParams) => {
          return isCellInSelection(params.rowIndex ?? -1, colIndex, selectionRangesRef.current);
        },
        'dbgo-cell-active': (params: CellClassParams) => {
          const ac = activeCellRef.current;
          return ac !== null && ac.row === (params.rowIndex ?? -1) && ac.col === colIndex;
        },
      },
      cellStyle: (params: CellClassParams): CellStyle | null | undefined => {
        const rowId = params.data?.[ROW_ID_FIELD] as number | undefined;
        if (rowId !== undefined) {
          const change = pendingChangesRef.current.get(rowId);

          // Deleted row: red left border + tint + strikethrough
          if (change?.type === 'delete') {
            return {
              backgroundColor: 'rgba(239, 83, 80, 0.10)',
              borderLeft: '3px solid #ef5350',
              color: '#ef5350',
              textDecoration: 'line-through',
            };
          }

          // Inserted row: green left border + tint
          if (change?.type === 'insert') {
            return {
              backgroundColor: 'rgba(61, 214, 140, 0.10)',
              borderLeft: '3px solid #3dd68c',
              color: 'var(--text-primary)',
            };
          }

          // Updated cell: orange left border + subtle tint on changed columns
          if (change?.type === 'update') {
            const modifiedCols = getModifiedColumns(rowId);
            if (params.colDef.field && modifiedCols.has(params.colDef.field)) {
              return {
                backgroundColor: 'rgba(224, 149, 92, 0.10)',
                borderLeft: '3px solid #e0955c',
                color: 'var(--text-primary)',
              };
            }
          }
        }

        // Default styling for null/number/boolean
        if (params.value === null || params.value === undefined) {
          return { color: 'var(--text-muted)', fontStyle: 'italic', opacity: '0.6' };
        }
        if (typeof params.value === 'number') {
          return { color: '#d19a66' };
        }
        if (typeof params.value === 'boolean') {
          return { color: '#c678dd' };
        }
        return null;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return 'NULL';
        if (typeof params.value === 'object') return JSON.stringify(params.value);
        return String(params.value);
      },
      // Object / array cells render as a compact chip so JSONB columns
      // and Mongo nested fields stay readable. Double-click expands to
      // the shared JSON viewer. valueFormatter above still feeds export
      // / clipboard with the stringified form.
      cellRenderer: JsonChipRenderer,
      cellRendererParams: {
        onOpen: (value: unknown, title: string) =>
          setJsonViewer({ value, title }),
      },
    }));

    // Apply column config (visibility + order)
    let orderedCols = dataCols;
    if (columnConfig.length > 0) {
      const configMap = new Map(columnConfig.map((cc, i) => [cc.field, { ...cc, order: i }]));
      orderedCols = dataCols
        .filter((col) => {
          const cc = configMap.get(col.field as string);
          return !cc || cc.visible;
        })
        .sort((a, b) => {
          const aOrder = configMap.get(a.field as string)?.order ?? 999;
          const bOrder = configMap.get(b.field as string)?.order ?? 999;
          return aOrder - bOrder;
        });
    }

    return [rowNumCol, ...orderedCols];
    // pendingChanges in deps forces AG Grid to get new ColDef objects,
    // which makes it re-evaluate cellStyle for every cell
  }, [data?.columns, getModifiedColumns, pendingChanges, columnConfig]);

  // ── Selected data for export ──────────────────────────────
  const hasSelection = selectionRanges.length > 0 && selectionRanges.some(r => {
    const n = normalizeRange(r);
    return n.endRow >= n.startRow && n.endCol >= n.startCol;
  });

  const selectedExportData = useMemo(() => {
    if (!hasSelection || !data?.columns || !rowData?.length) return null;

    // Collect unique rows and columns from all selection ranges
    const selectedRows = new Set<number>();
    const selectedCols = new Set<number>();
    for (const range of selectionRanges) {
      const n = normalizeRange(range);
      for (let r = n.startRow; r <= n.endRow; r++) selectedRows.add(r);
      for (let c = n.startCol; c <= n.endCol; c++) selectedCols.add(c);
    }

    const sortedCols = Array.from(selectedCols).sort((a, b) => a - b);
    const sortedRows = Array.from(selectedRows).sort((a, b) => a - b);

    const columns = sortedCols
      .filter(c => c >= 0 && c < data.columns.length)
      .map(c => ({ name: data.columns[c].name, type: data.columns[c].type }));

    const rows = sortedRows
      .filter(r => r >= 0 && r < rowData.length)
      .map(r => {
        const row: Record<string, unknown> = {};
        for (const col of columns) {
          row[col.name] = rowData[r][col.name];
        }
        return row;
      });

    return { columns, rows };
  }, [hasSelection, selectionRanges, data?.columns, rowData]);

  const selectionCellCount = useMemo(() => {
    if (!hasSelection) return 0;
    let count = 0;
    for (const range of selectionRanges) {
      const n = normalizeRange(range);
      count += (n.endRow - n.startRow + 1) * (n.endCol - n.startCol + 1);
    }
    return count;
  }, [hasSelection, selectionRanges]);

  const totalRows = data?.totalRows ?? data?.rowCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

  // ── Selection info for status bar ──────────────────────────

  const selectionInfo = useMemo(() => {
    if (selectionRanges.length === 0) return null;
    const range = normalizeRange(selectionRanges[0]);
    const rows = range.endRow - range.startRow + 1;
    const cols = range.endCol - range.startCol + 1;
    const total = rows * cols;
    if (total === 1) return '1 cell';
    return `${rows}\u00D7${cols} (${total} cells)`;
  }, [selectionRanges]);

  // ── Transpose Data ────────────────────────────────────────

  const transposedData = useMemo(() => {
    if (!transposed || !data?.columns || !rowData.length) return null;

    const maxRows = Math.min(rowData.length, 20);
    const cols = visibleColumns;

    // Build transposed column defs.
    //
    // `cellDataType: false` is critical: AG Grid v32 infers a cell type
    // from the first row's value and then rejects later values that don't
    // match (rendering them as "Invalid Number" / "Invalid Date"). In the
    // transposed view a single column holds values from many original
    // columns of mixed types — strings, numbers, dates, JSON — so type
    // inference is wrong by construction. Disable it and format manually
    // the same way as the main grid.
    const formatTransposedCell = (params: { value: unknown }) => {
      if (params.value === null || params.value === undefined) return 'NULL';
      if (typeof params.value === 'object') return JSON.stringify(params.value);
      return String(params.value);
    };

    const tColDefs: ColDef[] = [
      {
        field: '__field',
        headerName: 'Field',
        pinned: 'left',
        width: 160,
        sortable: false,
        cellDataType: false,
      },
      ...Array.from({ length: maxRows }, (_, i): ColDef => ({
        field: `__row_${i}`,
        headerName: `Row ${i + 1}`,
        minWidth: 120,
        sortable: false,
        cellDataType: false,
        valueFormatter: formatTransposedCell,
      })),
    ];

    // Build transposed row data
    const tRows = cols.map((col) => {
      const row: Record<string, unknown> = { __field: col.name };
      for (let i = 0; i < maxRows; i++) {
        row[`__row_${i}`] = rowData[i]?.[col.name] ?? null;
      }
      return row;
    });

    return { columnDefs: tColDefs, rowData: tRows };
  }, [transposed, data?.columns, rowData, visibleColumns]);

  // ── Editing Cell Rect ──────────────────────────────────────

  const editingCellRect = editingCell ? getEditingCellRect() : null;

  return (
    <div className="flex flex-col h-full bg-dbgo-workarea">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 h-[38px] bg-dbgo-panel border-b flex-shrink-0">
        {filterMode === 'raw' && (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Search size={12} className="text-text-muted flex-shrink-0" />
            <span className="text-2xs text-text-muted flex-shrink-0">WHERE</span>
            <input
              ref={whereInputRef}
              type="text"
              value={whereInput}
              onChange={(e) => {
                setWhereInput(e.target.value);
                updateCursor('where', e.target);
              }}
              onKeyUp={(e) => updateCursor('where', e.currentTarget)}
              onClick={(e) => updateCursor('where', e.currentTarget)}
              onFocus={(e) => {
                setActiveAutocomplete('where');
                updateCursor('where', e.currentTarget);
              }}
              onBlur={() => setActiveAutocomplete(null)}
              onKeyDown={(e) => handleAutocompleteKeyDown(e, 'where')}
              placeholder={isDocDriver
                ? 'e.g. { status: "active" }  (Mongo filter — Enter to apply)'
                : "e.g. status = 'active'  (Enter to apply)"}
              className="bg-dbgo-input border rounded-sm px-2 py-0.5 text-xs text-text-primary placeholder:text-text-muted outline-none flex-1 min-w-[120px] focus:border-focus"
            />
          </div>
        )}
        {filterMode === 'builder' && (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Filter size={12} className="text-accent-blue flex-shrink-0" />
            <span className="text-2xs text-accent-blue flex-shrink-0">Visual Filter</span>
            {whereInput && (
              <span className="text-2xs text-text-muted font-mono truncate flex-1 min-w-0">
                {whereInput}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1.5 min-w-0">
          <ArrowUpDown size={12} className="text-text-muted flex-shrink-0" />
          <span className="text-2xs text-text-muted flex-shrink-0">ORDER BY</span>
          <input
            ref={orderByInputRef}
            type="text"
            value={orderByInput}
            onChange={(e) => {
              setOrderByInput(e.target.value);
              updateCursor('orderby', e.target);
            }}
            onKeyUp={(e) => updateCursor('orderby', e.currentTarget)}
            onClick={(e) => updateCursor('orderby', e.currentTarget)}
            onFocus={(e) => {
              setActiveAutocomplete('orderby');
              updateCursor('orderby', e.currentTarget);
            }}
            onBlur={() => setActiveAutocomplete(null)}
            onKeyDown={(e) => handleAutocompleteKeyDown(e, 'orderby')}
            placeholder={isDocDriver ? '{ createdAt: -1 }' : 'e.g. created_at DESC'}
            className="bg-dbgo-input border rounded-sm px-2 py-0.5 text-xs text-text-primary placeholder:text-text-muted outline-none min-w-[120px] max-w-[200px] focus:border-focus"
          />
        </div>

        {/* Autocomplete popup — only one at a time, anchored to whichever
            input is focused. Hidden when the autocomplete hook reports
            `visible: false` (still in the typing-debounce window) or
            when there are zero suggestions for the current context. */}
        {activeAutocomplete === 'where' && whereAutocomplete.visible &&
         whereAutocomplete.suggestions.length > 0 && (
          <FilterAutocompletePopup
            suggestions={whereAutocomplete.suggestions}
            selectedIndex={Math.min(whereSuggestionIndex, whereAutocomplete.suggestions.length - 1)}
            filter={whereInput.slice(whereAutocomplete.replaceRange.start, whereAutocomplete.replaceRange.end)}
            anchor={whereInputRef.current}
            onSelect={(i) => applyAutocomplete('where', i)}
            onHover={(i) => setWhereSuggestionIndex(i)}
          />
        )}
        {activeAutocomplete === 'orderby' && orderByAutocomplete.visible &&
         orderByAutocomplete.suggestions.length > 0 && (
          <FilterAutocompletePopup
            suggestions={orderByAutocomplete.suggestions}
            selectedIndex={Math.min(orderBySuggestionIndex, orderByAutocomplete.suggestions.length - 1)}
            filter={orderByInput.slice(orderByAutocomplete.replaceRange.start, orderByAutocomplete.replaceRange.end)}
            anchor={orderByInputRef.current}
            onSelect={(i) => applyAutocomplete('orderby', i)}
            onHover={(i) => setOrderBySuggestionIndex(i)}
          />
        )}

        {/* Filter mode toggle. The visual builder generates SQL fragments,
            so it's SQL-only — document drivers stay on the raw-input
            mode where the user writes a mongosh filter object directly. */}
        {!isDocDriver && (
          <button
            onClick={() => setFilterMode((m) => (m === 'raw' ? 'builder' : 'raw'))}
            className={`w-7 h-7 flex items-center justify-center rounded-sm transition-colors ${
              filterMode === 'builder'
                ? 'text-accent-blue bg-accent-blue/10'
                : 'text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover'
            }`}
            title={filterMode === 'raw' ? 'Switch to visual filter builder' : 'Switch to raw WHERE input'}
          >
            <Filter size={14} />
          </button>
        )}

        {/* Column Manager */}
        <button
          onClick={() => setShowColumnManager(true)}
          className={`relative w-7 h-7 flex items-center justify-center rounded-sm transition-colors ${
            isColumnConfigModified ? 'text-accent-blue' : 'text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover'
          }`}
          title="Manage Columns"
        >
          <Columns3 size={14} />
          {isColumnConfigModified && (
            <div className="absolute top-0.5 right-0.5 w-[6px] h-[6px] rounded-full bg-accent-blue" />
          )}
        </button>

        {/* Transpose */}
        <button
          onClick={() => setTransposed(!transposed)}
          className={`w-7 h-7 flex items-center justify-center rounded-sm transition-colors ${
            transposed ? 'text-accent-blue bg-accent-blue/10' : 'text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover'
          }`}
          title={transposed ? 'Normal View' : 'Transpose View'}
        >
          <ArrowLeftRight size={14} />
        </button>

        {/* Separator */}
        <div className="w-px h-4 bg-[var(--border-default)]" />

        {/* Add Row */}
        <button
          onClick={() => handleAddRow()}
          disabled={!hasRowIdentity}
          className="w-7 h-7 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:text-text-muted disabled:hover:bg-transparent"
          title={hasRowIdentity ? 'Add Row' : 'Read-only: table has no primary key'}
        >
          <Plus size={14} />
        </button>

        {/* Delete Row */}
        <button
          onClick={handleDeleteRow}
          disabled={!hasSelection || !hasRowIdentity}
          className="w-7 h-7 flex items-center justify-center rounded-sm text-text-muted hover:text-accent-red hover:bg-dbgo-tab-hover transition-colors disabled:opacity-30 disabled:cursor-default disabled:hover:text-text-muted disabled:hover:bg-transparent"
          title={hasRowIdentity ? 'Delete Selected Row' : 'Read-only: table has no primary key'}
        >
          <Trash2 size={14} />
        </button>

        {/* Separator */}
        <div className="w-px h-4 bg-[var(--border-default)]" />

        {/* Apply Changes — opens review modal */}
        <button
          onClick={() => setShowReviewModal(true)}
          disabled={pendingChangeCount === 0 || isApplying}
          className="relative w-7 h-7 flex items-center justify-center rounded-sm transition-colors disabled:opacity-30 disabled:cursor-default bg-accent-green/10 text-accent-green hover:bg-accent-green/20 disabled:bg-transparent disabled:text-text-muted"
          title={`Review & Apply Changes (${pendingChangeCount})`}
        >
          <Check size={14} />
          {pendingChangeCount > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 text-[9px] bg-accent-green text-white rounded-full flex items-center justify-center">
              {pendingChangeCount}
            </span>
          )}
        </button>

        {/* Revert */}
        <button
          onClick={handleRevert}
          disabled={pendingChangeCount === 0}
          className="w-7 h-7 flex items-center justify-center rounded-sm transition-colors disabled:opacity-30 disabled:cursor-default bg-accent-red/10 text-accent-red hover:bg-accent-red/20 disabled:bg-transparent disabled:text-text-muted"
          title="Revert Changes"
        >
          <Undo2 size={14} />
        </button>

        {/* Separator */}
        <div className="w-px h-4 bg-[var(--border-default)]" />

        {/* Export */}
        <button
          onClick={() => setShowExportModal(true)}
          disabled={!data?.rows?.length}
          className="h-7 px-1.5 flex items-center gap-1 rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40"
          title={hasSelection ? `Export ${selectionCellCount} selected cells` : 'Export Data'}
        >
          <Download size={14} />
          {hasSelection && (
            <span className="text-[10px] text-accent-blue font-mono">{selectionCellCount}</span>
          )}
        </button>

        {/* Reload */}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="w-7 h-7 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40"
          title="Reload"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filter builder panel */}
      {filterMode === 'builder' && (
        <FilterBuilder
          columns={columnNames}
          onApply={handleFilterBuilderApply}
          initialWhere={tab.where}
        />
      )}

      {/* Read-only notice — table has no primary key */}
      {showNoIdentityNotice && (
        <div className="flex items-start gap-2 px-3 py-1.5 bg-accent-orange/10 border-b border-accent-orange/30 text-[11px] leading-relaxed text-accent-orange">
          <Lock size={12} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="font-medium">Read-only — this table has no primary key.</span>{' '}
            DBGo identifies a row by its primary key; without one it cannot target a single row,
            so grid editing is off. Use the query editor and write an explicit WHERE.
          </span>
        </div>
      )}

      {/* Apply error banner */}
      {applyError && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-red/10 border-b border-accent-red/30 text-xs text-accent-red">
          <span className="flex-1 min-w-0 truncate">Error: {applyError}</span>
          <button
            onClick={() => setApplyError(null)}
            className="text-accent-red hover:text-white text-xs underline flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Grid */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={gridContainerRef}
        className={`flex-1 min-h-0 ${appTheme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} ag-theme-dbgo outline-none`}
        style={{
          height: '100%',
          '--ag-font-family': gridFontFamily,
          '--ag-font-size': `${gridFontSize}px`,
          '--ag-row-height': `${gridRowHeight}px`,
          '--ag-header-height': `${gridHeaderHeight}px`,
          '--ag-cell-horizontal-padding': `${gridCellPadding}px`,
          '--ag-background-color': gridTheme.colors.background,
          '--ag-header-background-color': gridTheme.colors.headerBackground,
          '--ag-odd-row-background-color': gridTheme.colors.oddRowBackground,
          '--ag-row-hover-color': gridTheme.colors.rowHover,
          '--ag-selected-row-background-color': gridTheme.colors.selectedRow,
          '--ag-border-color': gridTheme.colors.border,
          '--ag-header-foreground-color': gridTheme.colors.headerForeground,
          '--ag-foreground-color': gridTheme.colors.foreground,
        } as React.CSSProperties}
        tabIndex={0}
        onKeyDown={handleGridKeyDown}
      >
        {error ? (
          <div className="flex items-center justify-center h-full text-sm text-accent-red">
            {error instanceof Error ? error.message : 'Query failed'}
          </div>
        ) : isLoading ? (
          <SkeletonTable rows={10} cols={5} />
        ) : visibleColumns.length === 0 && columnConfig.length > 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-text-muted">
            All columns are hidden. Open the Column Manager to show columns.
          </div>
        ) : transposed && transposedData ? (
          <AgGridReact
            key={`transposed-${gridGeneration}`}
            rowData={transposedData.rowData}
            columnDefs={transposedData.columnDefs}
            suppressMovableColumns={true}
            suppressCellFocus={true}
            animateRows={false}
            suppressContextMenu={true}
            suppressRowClickSelection={true}
            onGridReady={onGridReady}
          />
        ) : (
          <AgGridReact
            key={gridGeneration}
            rowData={rowData}
            columnDefs={columnDefs}
            suppressMovableColumns={false}
            suppressCellFocus={true}
            animateRows={false}
            suppressContextMenu={true}
            suppressRowClickSelection={true}
            onCellClicked={handleCellClick}
            onCellDoubleClicked={handleCellDoubleClick}
            onCellContextMenu={handleCellContextMenu}
            onGridReady={onGridReady}
            onSortChanged={onSortChanged}
            onDragStopped={onDragStopped}
            rowSelection="multiple"
            getRowId={(params) => String(params.data[ROW_ID_FIELD])}
            suppressColumnVirtualisation={false}
            rowBuffer={10}
            debounceVerticalScrollbar={true}
          />
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 h-[28px] bg-dbgo-panel border-t flex-shrink-0">
        <div className="flex items-center gap-3 text-[11px] text-text-secondary">
          <span>
            Rows: <span className="text-text-primary font-mono">{data?.rowCount ?? 0}</span>
          </span>
          {data?.durationMs !== undefined && (
            <span>
              Time: <span className="text-text-primary font-mono">{data.durationMs}ms</span>
            </span>
          )}
          {selectionInfo && (
            <span className="text-accent-blue">{selectionInfo}</span>
          )}
          {pendingChangeCount > 0 && (
            <span className="text-accent-orange text-[11px]">
              {pendingChangeCount} pending change{pendingChangeCount !== 1 ? 's' : ''}
            </span>
          )}
          {primaryKeyColumns.length > 0 ? (
            <span className="text-text-muted">
              PK: {primaryKeyColumns.join(', ')}
            </span>
          ) : showNoIdentityNotice ? (
            <span className="text-accent-orange">No PK — read-only</span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-1.5 py-0.5 text-[11px] text-text-secondary hover:text-text-primary disabled:opacity-30 rounded-sm hover:bg-dbgo-tab-hover transition-colors"
          >
            Prev
          </button>
          <span className="text-[11px] text-text-muted px-1">
            <span className="text-text-primary font-mono">{page}</span>
            {' / '}
            <span className="font-mono">{totalPages}</span>
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-1.5 py-0.5 text-[11px] text-text-secondary hover:text-text-primary disabled:opacity-30 rounded-sm hover:bg-dbgo-tab-hover transition-colors"
          >
            Next
          </button>
        </div>
      </div>

      {/* Cell Editor Overlay */}
      {editingCell && editingCellRect && data?.columns[editingCell.col] && (
        <CellEditor
          value={rowData[editingCell.row]?.[data.columns[editingCell.col].name] ?? null}
          columnName={data.columns[editingCell.col].name}
          columnType={data.columns[editingCell.col].type}
          initialKey={editingCell.initialKey}
          forceModal={editingCell.forceModal}
          cellRect={editingCellRect}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onSaveAndMove={handleEditorSaveAndMove}
        />
      )}

      {/* Cell Context Menu */}
      {cellContextMenu && (
        <ContextMenu
          items={contextMenuItems}
          position={cellContextMenu.position}
          onClose={() => setCellContextMenu(null)}
        />
      )}

      {showExportModal && (
        <ExportModal
          columns={
            hasSelection && selectedExportData
              ? selectedExportData.columns
              : transposed && transposedData
                ? transposedData.columnDefs
                    .map((cd) => ({ name: cd.field ?? '', type: 'text' }))
                    .filter((c) => c.name !== '')
                : visibleColumns.map((c) => ({ name: c.name, type: c.type }))
          }
          rows={
            hasSelection && selectedExportData
              ? selectedExportData.rows
              : transposed && transposedData
                ? transposedData.rowData
                : data?.rows?.map((row) => {
                    const filtered: Record<string, unknown> = {};
                    for (const col of visibleColumns) {
                      filtered[col.name] = row[col.name];
                    }
                    return filtered;
                  }) ?? []
          }
          totalRows={
            hasSelection && selectedExportData
              ? selectedExportData.rows.length
              : transposed && transposedData
                ? transposedData.rowData.length
                : data?.totalRows
          }
          defaultTableName={`${tab.schema}.${tab.table}`}
          defaultFilename={tab.table}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Header Context Menu */}
      {headerContextMenu && (
        <ContextMenu
          items={headerContextMenuItems}
          position={headerContextMenu.position}
          onClose={() => setHeaderContextMenu(null)}
        />
      )}

      {/* Column Manager Modal */}
      {showColumnManager && data?.columns && (
        <ColumnManager
          columns={data.columns.map((c) => ({ field: c.name, type: c.type }))}
          config={columnConfig}
          onConfigChange={handleColumnConfigChange}
          onClose={() => setShowColumnManager(false)}
        />
      )}

      {/* Review Changes Modal */}
      {showReviewModal && (
        <ReviewChangesModal
          statements={isDocDriver ? generateChangeMongosh() : generateChangeSQL()}
          onExecute={handleApply}
          onCopySQL={() => {/* toast handled inside modal */}}
          onCancel={() => setShowReviewModal(false)}
          loading={isApplying}
        />
      )}

      {/* JSON viewer — opened from a JsonChipRenderer double-click */}
      {jsonViewer && (
        <JsonViewerModal
          value={jsonViewer.value}
          title={jsonViewer.title}
          onClose={() => setJsonViewer(null)}
        />
      )}
    </div>
  );
}
